import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AnalysisDashboard.css';

const AnalysisDashboard = () => {
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);

    // Mock data for demonstration - replace with real API calls
    const mockAnalysesData = [
        {
            id: 'analysis_123',
            project_name: 'Highway LiDAR Analysis',
            project_id: 'project_789',
            status: 'running',
            start_time: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            estimated_completion: new Date(Date.now() + 1 * 60 * 60 * 1000), // 1 hour from now
            progress: {
                percentage: 65,
                completed_steps: 3,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'running',
                    progress: 75,
                    started_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
                    completed_at: null,
                    duration: null,
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['highway_section_1.las', 'highway_section_2.las'],
            user_id: 'user_456',
            project_id: 'project_789'
        },
        {
            id: 'analysis_456',
            project_name: 'Urban Road Analysis',
            project_id: 'project_789',
            status: 'completed',
            start_time: new Date(Date.now() - 4 * 60 * 60 * 1000),
            completed_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
            progress: {
                percentage: 100,
                completed_steps: 6,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 4 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 3 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 3 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 2.5 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 2.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['urban_road_1.las', 'urban_road_2.las'],
            user_id: 'user_456',
            project_id: 'project_789'
        },
        {
            id: 'analysis_789',
            project_name: 'Bridge Analysis Project',
            project_id: 'project_452',
            status: 'running',
            start_time: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
            estimated_completion: new Date(Date.now() + 3 * 60 * 60 * 1000), // 3 hours from now
            progress: {
                percentage: 25,
                completed_steps: 1,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 30 * 60 * 1000),
                    completed_at: new Date(Date.now() - 25 * 60 * 1000),
                    duration: 300, // 5 minutes
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'running',
                    progress: 50,
                    started_at: new Date(Date.now() - 25 * 60 * 1000),
                    completed_at: null,
                    duration: null,
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['bridge_section.las'],
            user_id: 'user_456',
            project_id: 'project_452'
        },
        {
            id: 'analysis_486',
            project_name: 'West Kingman Interchange Analysis',
            project_id: 'project_486',
            status: 'pending',
            start_time: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
            estimated_completion: new Date(Date.now() + 4 * 60 * 60 * 1000), // 4 hours from now
            progress: {
                percentage: 0,
                completed_steps: 0,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['west_kingman.las'],
            user_id: 'user_456',
            project_id: 'project_486'
        },
        {
            id: 'analysis_416',
            project_name: 'SR 516 Lake-Orange County Analysis',
            project_id: 'project_416',
            status: 'completed',
            start_time: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
            completed_at: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            progress: {
                percentage: 100,
                completed_steps: 6,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 6 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 5.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 5.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 4.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 4.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 4 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 4 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['sr_516_lake_orange.las'],
            user_id: 'user_456',
            project_id: 'project_416'
        }
    ];

    // Mock projects data
    const mockProjectsData = [
        {
            id: 'project_789',
            name: 'CFX SR-538 Poinciana Pkwy',
            description: 'Comprehensive analysis of highway sections for maintenance planning',
            total_analyses: 2,
            completed_analyses: 1,
            running_analyses: 1,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
        {
            id: 'project_452',
            name: 'ADOT Climbing Passing RSA',
            description: 'Structural analysis of bridge components using LiDAR data',
            total_analyses: 1,
            completed_analyses: 0,
            running_analyses: 1,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
        {
            id: 'project_486',
            name: 'ADOT West Kingman Interchange',
            description: 'Structural analysis of bridge components using LiDAR data',
            total_analyses: 1,
            completed_analyses: 0,
            running_analyses: 0,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
        {
            id: 'project_416',
            name: 'CFX SR 516 Lake-Orange County',
            description: 'Structural analysis of bridge components using LiDAR data',
            total_analyses: 1,
            completed_analyses: 1,
            running_analyses: 0,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
    ];

    useEffect(() => {
        // Simulate API call
        setTimeout(() => {
            setAnalyses(mockAnalysesData);
            setProjects(mockProjectsData);
            setSelectedProject(mockProjectsData[0]); // Select first project by default
            setLoading(false);
        }, 1000);
    }, []);

    // Filter analyses by selected project
    const filteredAnalyses = selectedProject 
        ? analyses.filter(analysis => analysis.project_id === selectedProject.id)
        : [];



    const getStatusClass = (status) => {
        switch (status) {
            case 'completed':
                return 'status-completed';
            case 'running':
                return 'status-running';
            case 'failed':
                return 'status-failed';
            case 'pending':
                return 'status-pending';
            default:
                return 'status-pending';
        }
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
        });
    };

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const formatDate = (date) => {
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h ago`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d ago`;
        
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };



    if (loading) {
        return (
            <div className="dashboard-page">
                <div className="dashboard-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading analysis dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <div className="dashboard-error">
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                {/* Main Content Area */}
                <div className="main-content-area-dashboard">
                    {/* Analysis List */}
                    <div className="analyses-list">
                        {filteredAnalyses.length === 0 ? (
                            <div className="no-analyses">
                                <p>No analyses found for the selected project. Start a new analysis from the RSA Analysis page.</p>
                            </div>
                        ) : (
                            <div className="analyses-container">
                                {/* Analysis Card based on Project Status */}
                                <div className={`analysis-card ${getProjectStatusClass(selectedProject)}`}>
                                    {/* Pagination Info at Top of Card */}
                          

                                                                         {/* Top Section - Progress Circles */}
                                     <div className="progress-circles-section">
                                         <div className="progress-circles-container">
                                             {(() => {
                                                 // Get the appropriate analysis data based on project status
                                                 let analysisData;
                                                 if (selectedProject.running_analyses > 0) {
                                                     // Show running analysis
                                                     analysisData = filteredAnalyses.find(a => a.status === 'running') || filteredAnalyses[0];
                                                 } else if (selectedProject.completed_analyses === selectedProject.total_analyses) {
                                                     // Show completed analysis
                                                     analysisData = filteredAnalyses.find(a => a.status === 'completed') || filteredAnalyses[0];
                                                 } else {
                                                     // Show pending analysis
                                                     analysisData = filteredAnalyses.find(a => a.status === 'pending') || filteredAnalyses[0];
                                                 }
                                                 
                                                 return analysisData.steps.map((step, index) => (
                                                     <div key={index} className="progress-step">
                                                         <div className={`progress-circle ${getStatusClass(step.status)}`}>
                                                             {step.status === 'completed' ? (
                                                                 <span className="progress-checkmark">✓</span>
                                                             ) : (
                                                                 <span className="circle-number">{index + 1}</span>
                                                             )}
                                                         </div>
                                                         <div className="step-label" dangerouslySetInnerHTML={{ 
                                                             __html: step.display_name === 'File Tiling' ? 'File<br>Tiling' : step.display_name 
                                                         }}></div>

                                                     </div>
                                                 ));
                                             })()}
                                         </div>
                                     </div>

                                                                         {/* Bottom Section - Active Step Details */}
                                     <div className="active-step-section">
                                         {(() => {
                                             // Get the appropriate analysis data based on project status
                                             let analysisData;
                                             if (selectedProject.running_analyses > 0) {
                                                 // Show running analysis
                                                 analysisData = filteredAnalyses.find(a => a.status === 'running') || filteredAnalyses[0];
                                             } else if (selectedProject.completed_analyses === selectedProject.total_analyses) {
                                                 // Show completed analysis
                                                 analysisData = filteredAnalyses.find(a => a.status === 'completed') || filteredAnalyses[0];
                                             } else {
                                                 // Show pending analysis
                                                 analysisData = filteredAnalyses.find(a => a.status === 'pending') || filteredAnalyses[0];
                                             }
                                             
                                             const activeStep = analysisData.steps.find(step => step.status === 'running') || 
                                                              analysisData.steps.find(step => step.status === 'pending') ||
                                                              analysisData.steps[analysisData.steps.length - 1];
                                            
                                            return (
                                                <div className="active-step-content">
                                                    <div className="active-step-header">
                                                        <h2>{activeStep.display_name}</h2>
                                                        <div className={`active-step-status ${getStatusClass(activeStep.status)}`}>
                                                            {activeStep.status.toUpperCase()}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="active-step-description">
                                                        <p>{activeStep.description}</p>
                                                    </div>

                                                    {activeStep.status === 'running' && (
                                                        <div className="active-step-progress">
                                                            <div className="step-progress-header">
                                                                <span>Progress</span>
                                                                <span className="step-progress-percentage">{activeStep.progress}%</span>
                                                            </div>
                                                            <div className="step-progress-bar">
                                                                <div 
                                                                    className="step-progress-fill"
                                                                    style={{ width: `${activeStep.progress}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="active-step-times">
                                                        {activeStep.started_at && (
                                                            <div className="time-info">
                                                                <span className="time-label">Started:</span>
                                                                <span className="time-value">{formatTime(activeStep.started_at)}</span>
                                                            </div>
                                                        )}
                                                        {activeStep.completed_at && (
                                                            <div className="time-info">
                                                                <span className="time-label">Completed:</span>
                                                                <span className="time-value">{formatTime(activeStep.completed_at)}</span>
                                                            </div>
                                                        )}
                                                        {activeStep.duration && (
                                                            <div className="time-info">
                                                                <span className="time-label">Duration:</span>
                                                                <span className="time-value">{formatDuration(activeStep.duration)}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                                                                         {/* Analysis Meta Info */}
                                                     <div className="analysis-meta-info">
                                                         <div className="meta-item">
                                                             <span className="meta-label">Project:</span>
                                                             <span className="meta-value">{analysisData.project_name}</span>
                                                         </div>
                                                         <div className="meta-item">
                                                             <span className="meta-label">Overall Progress:</span>
                                                             <span className="meta-value">{analysisData.progress.percentage}%</span>
                                                         </div>
                                                         <div className="meta-item">
                                                             <span className="meta-label">Steps Completed:</span>
                                                             <span className="meta-value">{analysisData.progress.completed_steps} of {analysisData.progress.total_steps}</span>
                                                         </div>
                                                     </div>

                                                                                                         {/* Action Buttons */}
                                                     <div className="analysis-actions">
                                                         {analysisData.status === 'completed' && (
                                                             <>
                                                                 <button className="action-button primary">View Report</button>
                                                                 <button className="action-button secondary">Download Report</button>
                                                             </>
                                                         )}
                                                         
                                                         {analysisData.status === 'running' && (
                                                             <button className="action-button warning">Cancel Analysis</button>
                                                         )}
                                                     </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                                             </div>
                        )}
                    </div>
                </div>

                {/* Right Side Panel - Projects List */}
                <div className="right-panel">
                    <div className="projects-panel">
                        <div className="analysis-dashboard-panel-header">
                            <h3>Projects</h3>
                            <span className="project-count">{projects.length} Projects</span>
                        </div>
                        
                        <div className="projects-list">
                            {projects.map((project, index) => (
                                <div 
                                    key={`${project.id}-${index}`} 
                                    className={`project-item ${selectedProject?.id === project.id ? 'selected' : ''} ${getProjectStatusClass(project)}`}
                                    onClick={() => setSelectedProject(project)}
                                >
                                    <div className="analysis-dashboard-project-header">
                                        <h4>{project.name}</h4>
                                        <div className={`project-status ${getProjectStatusClass(project)}`}>
                                            {getProjectStatusText(project)}
                                        </div>
                                    </div>
                                    
                                    <p className="project-description">{project.description}</p>
                                    
                                    <div className="project-stats">
                                        <div className="stat-item">
                                            <span className="stat-label">Analyses:</span>
                                            <span className="stat-value">{project.total_analyses}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Completed:</span>
                                            <span className="stat-value completed">{project.completed_analyses}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Running:</span>
                                            <span className="stat-value running">{project.running_analyses}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="project-footer">
                                        <span className="last-updated">Updated: {formatDate(project.last_updated)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Helper functions for project status
    function getProjectStatusClass(project) {
        if (project.running_analyses > 0) return 'running';
        if (project.completed_analyses === project.total_analyses) return 'completed';
        return '';
    }

    function getProjectStatusText(project) {
        if (project.running_analyses > 0) return 'Running';
        if (project.completed_analyses === project.total_analyses) return 'Completed';
        return 'Pending';
    }
};

export default AnalysisDashboard;
